// Models behind the interactive features in the education guides.
// The timeline and wear curves are illustrative sketches of direction, never measured wear times;
// the guides label them that way wherever they appear.
import type { DiscoveryItem } from './discovery.js';
import { isReferenceNamed } from './catalogue-seo.js';

// ── What is oud? ────────────────────────────────────────────────────────────
export const oudStyles = [
  { id: 'rose', name: 'Rose & oud', terms: ['rose'], line: 'The classic pairing. Rose softens oud’s darker edges and gives it a jammy, velvety richness.' },
  { id: 'sweet', name: 'Sweet & ambery', terms: ['sweet', 'vanilla', 'amber', 'caramel', 'creamy'], line: 'Amber, vanilla and tonka round oud into something warm and enveloping. Often the easiest way in.' },
  { id: 'smoky', name: 'Smoky & leathery', terms: ['smoky', 'leather'], line: 'Leather and smoke push oud darker and drier, closer to the scent of burning wood chips.' },
  { id: 'spiced', name: 'Spiced & earthy', terms: ['warm spicy', 'spicy', 'earthy'], line: 'Saffron, cardamom and earthy woods keep oud dry and resinous rather than sweet.' },
  { id: 'fruity', name: 'Fruity', terms: ['fruity'], line: 'Fruit notes lift oud and add brightness, a more modern and playful take.' },
] as const;
export type OudStyleId = typeof oudStyles[number]['id'];
export type OudFragrance = Pick<DiscoveryItem, 'slug' | 'name' | 'concentration' | 'brand' | 'accords' | 'keyNotes' | 'matchScore'>;


const accordIntensity = (item: Pick<DiscoveryItem, 'accords'>, terms: readonly string[]) =>
  Math.max(0, ...item.accords.filter(accord => terms.some(term => accord.name.toLowerCase().includes(term))).map(accord => accord.intensity));

/** How strongly oud is recorded, on the 0–5 accord scale; a key note without an accord counts as 3. */
export function oudStrength(item: Pick<DiscoveryItem, 'accords' | 'keyNotes'>) {
  const accord = accordIntensity(item, ['oud']);
  return accord || (item.keyNotes.some(note => /oud|agar/i.test(note)) ? 3 : 0);
}

/** Fragrances whose recorded accords put them in a style, strongest pairing first. */
export function oudStyleMatches(items: readonly OudFragrance[], style: OudStyleId) {
  const terms = oudStyles.find(row => row.id === style)!.terms;
  // Editorial rule for this guide: list products under their own identity only.
  return items
    .filter(item => !isReferenceNamed(item.name) && oudStrength(item) > 0)
    .map(item => ({ item, pairing: accordIntensity(item, terms), oud: oudStrength(item) }))
    .filter(row => row.pairing >= 3)
    .sort((a, b) => b.pairing - a.pairing || b.oud - a.oud || a.item.name.localeCompare(b.item.name));
}

// ── Perfume notes explained ─────────────────────────────────────────────────
export const unfoldLayers = [
  { id: 'top', name: 'Top', notes: ['Bergamot', 'Pink pepper'] },
  { id: 'heart', name: 'Heart', notes: ['Rose', 'Jasmine'] },
  { id: 'base', name: 'Base', notes: ['Sandalwood', 'Musk', 'Vanilla'] },
] as const;
export type UnfoldLayer = typeof unfoldLayers[number]['id'];
export const unfoldMoments = [
  { hours: 0, label: 'First spray', line: 'Bright and sharp. The top notes lead while everything else is still arriving.' },
  { hours: 0.25, label: '15 minutes', line: 'The opening is softening and the floral heart is starting to show through.' },
  { hours: 1, label: '1 hour', line: 'The heart is in charge now. Most of the citrus has gone; the woods are quietly building underneath.' },
  { hours: 3, label: '3 hours', line: 'The drydown. Woods, musk and vanilla carry the scent, with a trace of the heart.' },
  { hours: 6, label: '6 hours', line: 'Close to the skin. What remains is mostly the base, soft and warm.' },
] as const;
export const unfoldHours = 7;

const unfoldShapes: Record<UnfoldLayer, (hours: number) => number> = {
  top: hours => Math.exp(-hours / 0.45),
  heart: hours => (0.3 + 0.7 * (1 - Math.exp(-hours / 0.3))) * Math.exp(-hours / 3.2),
  base: hours => (0.12 + 0.88 * (1 - Math.exp(-hours / 1.2))) * Math.exp(-hours / 14),
};
// Each layer is scaled so its own peak reads as 1.
const unfoldPeaks = Object.fromEntries(Object.entries(unfoldShapes).map(([layer, shape]) =>
  [layer, Math.max(...Array.from({ length: 141 }, (_, index) => shape(index * unfoldHours / 140)))])) as Record<UnfoldLayer, number>;

/** Relative prominence of each layer at a time, 0–1. A sketch: real timings vary with formula, skin and amount. */
export function unfoldProminence(layer: UnfoldLayer, hours: number) {
  return unfoldShapes[layer](hours) / unfoldPeaks[layer];
}

// ── Extrait, EDP and EDT ─────────────────────────────────────────────────────
// Ranges commonly quoted for each label. Brands set their own formulas and there is no single standard.
export const concentrationBands = [
  { id: 'eau-fraiche', name: 'Eau fraîche', short: 'Fraîche', from: 1, to: 3, line: 'A light splash, often sold for body or summer use. Expect a soft, short-lived wear.' },
  { id: 'cologne', name: 'Eau de Cologne', short: 'EDC', from: 3, to: 5, line: 'Traditionally a light, citrus-led style. Today the term also turns up on stronger modern releases, especially in men’s fragrance.' },
  { id: 'edt', name: 'Eau de Toilette', short: 'EDT', from: 5, to: 15, line: 'A wide middle ground. Many EDTs are bright and project well in the first hours.' },
  { id: 'edp', name: 'Eau de Parfum', short: 'EDP', from: 15, to: 20, line: 'The most common label for new releases. Often fuller and warmer than the same fragrance as an EDT.' },
  { id: 'extrait', name: 'Extrait de parfum', short: 'Extrait', from: 20, to: 40, line: 'The richest label. Often denser and closer to the skin, but not automatically louder or longer-lasting.' },
] as const;
export const concentrationMax = 40;
export function concentrationBand(percent: number) {
  return concentrationBands.find(band => percent < band.to) ?? concentrationBands[concentrationBands.length - 1]!;
}

// ── How to make perfume last longer ──────────────────────────────────────────
export const wearFactors = [
  { id: 'style', name: 'Fragrance', options: [
    { id: 'citrus', name: 'Fresh citrus', hold: 1.4, loud: 1, line: 'Citrus and fresh notes are made of light materials that evaporate quickly.' },
    { id: 'floral', name: 'Floral', hold: 3, loud: 1, line: 'A floral heart usually outlasts a citrus opening and settles into a softer base.' },
    { id: 'amber', name: 'Woody amber', hold: 5.5, loud: 1, line: 'Woods, resins and amber are heavier materials that linger on skin.' },
  ] },
  { id: 'skin', name: 'Skin', options: [
    { id: 'dry', name: 'Dry', hold: 0.85, loud: 1, line: 'Dry skin tends to let fragrance fade sooner.' },
    { id: 'moisturised', name: 'Moisturised', hold: 1.15, loud: 1, line: 'Moisturised skin tends to hold fragrance a little longer.' },
  ] },
  { id: 'placement', name: 'Where', options: [
    { id: 'rubbed', name: 'Wrists, rubbed', hold: 0.85, loud: 1.05, line: 'Rubbing warms the skin and rushes the opening.' },
    { id: 'neck', name: 'Neck & chest', hold: 1, loud: 1.1, line: 'Warm pulse points help the scent diffuse around you.' },
    { id: 'fabric', name: 'Clothing', hold: 1.6, loud: 0.85, line: 'Fabric holds scent longer than skin, though it can sit quieter.' },
  ] },
  { id: 'weather', name: 'Weather', options: [
    { id: 'cold', name: 'Cold', hold: 1.2, loud: 0.75, line: 'Cold slows evaporation: longer, but closer to the skin.' },
    { id: 'mild', name: 'Mild', hold: 1, loud: 1, line: 'Mild weather is the middle of the road.' },
    { id: 'hot', name: 'Hot', hold: 0.75, loud: 1.25, line: 'Heat makes a fragrance louder at first, then shorter-lived.' },
  ] },
] as const;
export type WearChoice = Record<typeof wearFactors[number]['id'], string>;
export const defaultWearChoice: WearChoice = { style: 'floral', skin: 'dry', placement: 'rubbed', weather: 'mild' };
export const wearHours = 10;

export function wearOptions(choice: WearChoice) {
  return wearFactors.map(factor => factor.options.find(option => option.id === choice[factor.id]) ?? factor.options[0]!);
}
/** Sketch of how noticeable a fragrance is to people nearby, and to the wearer, 0–1 over hours. */
export function wearCurves(choice: WearChoice) {
  const options = wearOptions(choice);
  const hold = options.reduce((value, option) => value * option.hold, 1);
  const loud = options.reduce((value, option) => value * option.loud, 1);
  const halfLife = hold;
  const others = (hours: number) => Math.min(1, loud * 0.92 * Math.pow(0.5, hours / halfLife));
  // The wearer's nose adapts within the first half hour and settles at a fraction of what is there.
  const adaptation = (hours: number) => 0.3 + 0.7 * Math.exp(-hours / 0.22);
  const self = (hours: number) => others(hours) * adaptation(hours);
  const noticeable = 0.12;
  const fadesAt = (curve: (hours: number) => number) => {
    for (let hours = 0; hours <= wearHours; hours += 0.05) if (curve(hours) < noticeable) return hours;
    return wearHours;
  };
  return { others, self, noticeable, othersFade: fadesAt(others), selfFade: fadesAt(self), lines: options.map(option => option.line) };
}
